import { SourceBook } from "@dndModels/misc/SourceBook";
import { DataType, SourceBookType, WorldSetting } from "@dndModels/Types";
import { APIClass } from "@utilities/smart_api_class";
import { User } from "@models/User";


// This mapping takes sb_id and gives a dominance level. The higher the dominance level the more dominant it is. 
export const CORE_DOMINANCE_PRIORITIES: { [sb_id: string]: number } = {
  "63cd69a14d337b1738dab548": 1, // SRD 5.1 
  "60a9c9ed891e971c782a76a1": 2, // PHB 5e
  "60a9c9dd891e971c782a76a0": 3, // DMG 5e
  "60a9ca9d891e971c782a76a3": 4, // MM 5e
  "695ae0f0cfa83df9124cb5ac": 5, // SRD 5.2.1 
  "6675b302a4476741138fa22b": 6, // PHB 5.5e
  "682f5daedc859b7e9b33c82e": 7, // DMG 5.5e
  "67a7afb7377680b79053207a": 8, // MM 5.5e
}
const core_ids = Object.keys(CORE_DOMINANCE_PRIORITIES);

export const OTHER_DOMINANCE_PRIORITIES: { [sb_id: string]: number } = {
  "60a9cacd891e971c782a76a6": 1, // E:RftLW - Artificer stuff
  "60a9cb87891e971c782a76ae": 2, // TCoE - Artificer stuff
  "693857d2771d29dec3205f4b": 3, // E:FotA - Artificer stuff
  "6199d5c9b8534e2268b9bc0f": 1, // FToD (No overlap with above)
  "62aa3d550310c722749ea16d": 1, // EEPC
  "639ceb0991bedb38606258b6": 1, // WbtW
  "6675c6cd7dda91b8cb2d56c7": 2, // MotM
  "6300d7cc8670ce0009ff026d": 1, // SaiS
  "60a9cbcb891e971c782a76b2": 1, // VRgtR
  "60a9ca0a891e971c782a76a2": 1, // XgtE
}
const non_core_ids = Object.keys(OTHER_DOMINANCE_PRIORITIES);

const anyOverlaps = (arr1: string[], arr2: string[]) => {
  for (let i = 0; i < arr1.length; ++i) {
    if (arr2.includes(arr1[i])) {
      return true;
    }
  }
  return false;
}

export abstract class UpsertableBase {
  data_type: DataType = "upsertable_base";
  _id = "";
  saved_without_api = false;
  name = "";
  description = "";

  constructor(obj?: any) {
    if (obj) {
      this._id = obj._id ? obj._id : "";
      this.name = obj.name ? obj.name.trim() : "";
      this.description = obj.description && obj.description !== "undefined" ? obj.description : "";
    }
  }

  get display_format(): string {
    return this.get_display_format();
  }

  // get detail_format(): string {
  //   return this.get_detail_format();
  // }

  get_detail_format() {
    return "";
  }

  get_display_format() {
    return "";
  }

  get description_format() {
    return "";
  }

  toDBObj(): any {
    return {
      _id: this._id,
      name: this.name,
      description: this.description,
    };
  }
  
  copy(copyMe: UpsertableBase, drop_ids = false): void {
    if (!drop_ids) {
      this._id = copyMe._id;
    }
    this.name = copyMe.name;
    this.description = copyMe.description;
  }

  abstract clone(drop_ids: boolean): UpsertableBase;

  get id() {
    return this._id;
  }
}

export interface Indexable {
  id: string;
  name: string;
  description: string;
}

export class ModelBase extends UpsertableBase implements Indexable {
  data_type: DataType = "model_base";
  notes = "";
  source_books: SourceBook[] = [];
  source_books_old: { type: SourceBookType, id: string, third_party: boolean }[] = [];
  owner_id = ""; // ID of the user who owns it.  If it's "" then it's made by an Admin
  variant_of_id = "";
  variant_group: ModelBase[] = []; // Populated on load
  variant_ids: string[] = []; 

  source_book_abbrev = "";
  template_only = false;
  image_url = "";
  // game_system: string = "D&D";
  reusable_string_ids: string[] = [];
  open_game_content = false;
  world_settings: WorldSetting[] = [];

  tags: string[] = [];
  // This is a flag that can be used to block all monster feature sources.  
  // It's meant to separate out races, classes, feats, etc that weren't meant for PCs, 
  // but are here so DMs can use them to make custom monsters.
  // But of course, DMs can choose to let their players use them if they want.
  get monster() {
    return this.tags.includes("Monster");
  }
  set monster(value: boolean) {
    if (value) {
      this.tags.push("Monster");
    } else {
      this.tags = this.tags.filter(o => o !== "Monster");
    }
  }

  loaded = false;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.notes = obj.notes ? obj.notes : "";
      
      // if (obj.source_books && !obj.name) {
      //   debugger;
      // }
      this.source_books_old = [];
      if (obj.source_books) {
        this.source_books_old = obj.source_books.map((o: any) => {
          return {
            id: o.id,
            type: o.type ? o.type : "Source Book", 
            third_party: o.third_party ? o.third_party : false
          };
        });
      } else if (obj.source_book_type && obj.source_book_type !== "" && 
        obj.source_book_id && obj.source_book_id !== "") {
        this.source_books_old.push({ type: obj.source_book_type, id: obj.source_book_id, third_party: false });
      } else if (obj.source_type && obj.source_type !== "" && 
        obj.source_id && obj.source_id !== "") {
        this.source_books_old.push({ type: obj.source_type, id: obj.source_id, third_party: false });
      }
      this.owner_id = obj.owner_id ? obj.owner_id : "";
      // if (obj.data_type === "game_class" && obj.name.includes("ruid")) {
      //   debugger;
      // }
      
      this.variant_of_id = obj.variant_of_id ? obj.variant_of_id : "";
      this.variant_ids = obj.variant_ids ? obj.variant_ids : [];
      // this.dominance_level = obj.dominance_level ? obj.dominance_level : 0;
      // if (this.dominance_level === 0 && obj.dominant_variant) {
      //   this.dominance_level = 1;
      // }
      // this.non_version_variant = obj.non_version_variant ? obj.non_version_variant : false;
      // this.use_base_name = obj.use_base_name ? obj.use_base_name : false;
      this.monster = obj.monster ? obj.monster : false;
      this.template_only = obj.template_only ? obj.template_only : false;
      this.image_url = obj.image_url ? obj.image_url : "";
      if (typeof(this.image_url) !== "string" || this.image_url.includes("[object")) {
        this.image_url = "";
      }
      this.reusable_string_ids = obj.reusable_string_ids ? obj.reusable_string_ids : [];
      this.open_game_content = obj.open_game_content ? obj.open_game_content : false;
      this.world_settings = obj.world_settings ? obj.world_settings : [];
      if (this.world_settings.includes("Any")) {
        this.world_settings = this.world_settings.filter(o => o !== "Any");
      }
      this.tags = obj.tags ? obj.tags : [];
    }
    // if (this.source_books.length === 0) {
    //   this.source_books.push({ type: "Homebrew", id: "Personal" });
    // }
  }

  // load(obj: any): void {
  //   if (obj) {
  //     this.loaded = true;
  //   }
  // }

  get id(): string {
    return this._id;
  }

  get tag_string() {
    return this.tags.join(", ");
  }

  get ua() {
    return this.source_books.every(o => o.ua);
  }

  get hb() {
    return this.source_books.every(o => o.homebrew);
  }

  get tp() {
    return this.source_books.every(o => o.third_party);
  }

  get blocked() {
    return this.source_books.every(o => o.blocked);
  }
  get archived() {
    return this.source_books.every(o => o.archived);
  }
  get free() {
    return this.source_books.every(o => o.is_free);
  }

  get_detail_format(): string {
    const tag_string = this.tag_string;
    let str = `{list_of_type:source_book;abbreviation;[${this.source_book_ids.join(",")}]}`;
    if (tag_string.length > 0) {
      str += `; ${tag_string}`;
    }
    return str.trim();
  }

  // get detail_format(): string {
  //   return this.get_detail_format() + (this.non_version_variant ? ` ${this.variant_of_name} Variant` : (this.is_dominant ? "" : " - Legacy"));
  // }

  get_display_format(): string {
    const str = `${super.get_display_format()} ${this.name}`;
    return str.trim();
  }

  // This makes it easier to search for the original and all variants. 
  // The purpose of it is to make it easier to see all variants together, 
  // which is important now that I'm adding dominant variants.
  get variant_group_id() {
    if (this.variant_of_id === "") {
      return this._id;
    }
    return this.variant_of_id;
  }
  
  // Gets the highest dominance level for the sb_ids given.

  getCoreDominanceLevel = () => {
    const sb_ids = this.source_book_ids.filter(o => core_ids.includes(o));
    return Math.max(...sb_ids.map(o => CORE_DOMINANCE_PRIORITIES[o]), -1);
  }
  getNonCoreDominanceLevel = () => {
    const sb_ids = this.source_book_ids.filter(o => non_core_ids.includes(o));
    return Math.max(...sb_ids.map(o => OTHER_DOMINANCE_PRIORITIES[o]), -1);
  }
  // It's a core object if it or any of its variants are in a core book.
  // If it's core then the dominant variant will be in a core book.
  isCore = () => {
    return this.thisIsCore() || this.variant_group.some(o => anyOverlaps(core_ids, o.source_book_ids));
  }
  thisIsCore = () => {
    return anyOverlaps(core_ids, this.source_book_ids);
  }
  isNonVersionVariant = () => { // I'm probably going to have to change this, but it works for now.
    return this.isCore() && !this.thisIsCore();
  }
  getDominant() {
    if (this.isCore()) {
      const max = Math.max(...this.variant_group.map(o => o.getCoreDominanceLevel()), this.getCoreDominanceLevel());
      const dom = this.variant_group.find(o => o.getCoreDominanceLevel() === max);
      if (dom) {
        return dom;
      }
      return this;
    } else {
      const max = Math.max(...this.variant_group.map(o => o.getNonCoreDominanceLevel()), this.getNonCoreDominanceLevel());
      const dom = this.variant_group.find(o => o.getNonCoreDominanceLevel() === max);
      if (dom) {
        return dom;
      }
      return this;
    }
  }
  isDominant() {
    // if (this.name.includes("Human")) {
    //   debugger;
    // }
    if (this.isCore()) {
      if (this.variant_group.length > 1) {
        const dominance_level = this.getCoreDominanceLevel();
        const max = Math.max(...this.variant_group.map(o => o.getCoreDominanceLevel()), dominance_level);
        return dominance_level === max;
      }
    } else {
      if (this.variant_group.length > 1) {
        const dominance_level = this.getNonCoreDominanceLevel();
        const max = Math.max(...this.variant_group.map(o => o.getNonCoreDominanceLevel()), dominance_level);
        return dominance_level === max;
      }
    }
    return true;
  }

  get is_dominant() {
    return this.isDominant();
  }

  detailFormat = () => {
    const dom = this.getDominant();
    if (dom._id === this._id) {
      return this.get_detail_format();
    } else {
      return this.get_detail_format() + (this.isNonVersionVariant() ? ` ${dom.name} Variant` : " - Legacy");
    }
  }

  get use_name(): string {
    return this.get_use_name();
  }

  get_use_name(): string {
    // if (this.variant_of_name === "") {
      return this.name;
    // } else {
    //   return this.variant_of_name;
    // }
  }

  get_homebrewery_string(api: APIClass): string {
    let str = `# ${this.true_name}\n\r`;
    str += `${this.description}\n\r`;
    return str;
  }

  get true_name(): string {
    // if (this.name.includes("Scout")) {
    //   console.log(this);
    // }
    // if (this.variant_of_name === "") {
    //   return this.name;
    // } else {
    //   let true_name = this.name;
    //   if (this.source_book_abbrev !== "") {
    //     if (this.variant_group_id === this._id) {
    //       true_name += ` (Legacy)`;
    //     } else {
    //       true_name += ` (${this.source_book_abbrev})`;
    //     }
    //   }
    //   if (!this.name.includes(this.variant_of_name)) {
    //     true_name += ` - Variant ${this.variant_of_name}`;
    //   }

    //   return true_name;
    //   // if (["",this.variant_of_name].includes(this.name) && this.source_book_abbrev !== "") {
    //   //  return `${this.variant_of_name} (${this.source_book_abbrev})`;
    //   // } else if (this.use_base_name) {
    //   //   return this.name + " " + this.variant_of_name;
    //   // } else {
    //   //   return this.variant_of_name;
    //   // }
    // }
    return this.name;
  }

  // get use_name(): string {
  //   return this.name;
  // }

  get source_book_ids(): string[] {
    return this.source_books_old.map(o => { return o.id; });
  }

  get source_book_types(): SourceBookType[] {
    const types: SourceBookType[] = [];
    for (const sb of this.source_books_old) {
      if (!types.includes(sb.type)) {
        types.push(sb.type);
      }
    }
    return types;
  }

  get search_ids(): string[] {
    if (this.variant_of_id !== "") {
      return [this._id,...this.variant_ids]; // , this.variant_of_id]; // 
    }
    return [this._id];
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      notes: this.notes,
      source_books: this.source_books_old,
      owner_id: this.owner_id,
      variant_of_id: this.variant_of_id,
      variant_ids: this.variant_ids,
      // dominance_level: this.dominance_level,
      // non_version_variant: this.non_version_variant,
      // use_base_name: this.use_base_name,
      // monster: this.monster,
      template_only: this.template_only,
      image_url: this.image_url,
      reusable_string_ids: this.reusable_string_ids,
      open_game_content: this.open_game_content,
      tags: this.tags,
      world_settings: this.world_settings
    };
  }

  toLight(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      notes: this.notes,
      source_books: this.source_books_old,
      owner_id: this.owner_id,
      variant_of_id: this.variant_of_id,
      variant_ids: this.variant_ids,
      // dominance_level: this.dominance_level,
      // non_version_variant: this.non_version_variant,
      // use_base_name: this.use_base_name,
      // monster: this.monster,
      template_only: this.template_only,
      image_url: this.image_url,
      reusable_string_ids: this.reusable_string_ids,
      open_game_content: this.open_game_content,
      tags: this.tags,
      world_settings: this.world_settings
    };
  }
  
  copy(copyMe: ModelBase, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.notes = copyMe.notes;
    this.variant_of_id = copyMe.variant_of_id;
    // this.variant_of_name = copyMe.variant_of_name;
    // this.dominance_level = copyMe.dominance_level;
    // this.non_version_variant = copyMe.non_version_variant;
    this.variant_ids = copyMe.variant_ids;
    // this.use_base_name = copyMe.use_base_name;
    this.source_book_abbrev = copyMe.source_book_abbrev;
    // this.monster = copyMe.monster;
    this.template_only = copyMe.template_only;
    if (!drop_ids) {
      const source_books = copyMe.source_books_old.map(sb => {
        return {...sb};
      });
      this.source_books_old = source_books;
      this.owner_id = copyMe.owner_id;
      this.variant_group = copyMe.variant_group.filter(o => o._id !== copyMe._id);
      this.variant_group.push(this);
    }
    this.image_url = copyMe.image_url;
    this.reusable_string_ids = copyMe.reusable_string_ids;
    this.open_game_content = copyMe.open_game_content;
    this.tags = [...copyMe.tags];
    this.world_settings = [...copyMe.world_settings];

    this.loaded = copyMe.loaded;
  }

  clone(drop_ids = false): ModelBase {
    const new_obj = new ModelBase();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class PlayerObject extends UpsertableBase {
  data_type: DataType = "player_object";
  owner_id = "";
  is_archived = false;
  is_favorite = false;
  image_url = "";
  owner: User | undefined = undefined;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.owner_id = obj.owner_id ? obj.owner_id : "";
      this.is_archived = obj.is_archived ? obj.is_archived : false;
      this.is_favorite = obj.is_favorite ? obj.is_favorite : false;
      this.image_url = obj.image_url ? obj.image_url : "";
      this._owner_name = obj._owner_name ? obj._owner_name : "";
    }
  }

  get use_name() {
    return this.name;
  }

  _owner_name = "";
  get owner_name() {
    if (this.owner) {
      return this.owner.profile_name;
    }
    return this._owner_name;
  }

  get owner_string() {
    if (this.owner_name.length > 0) {
      return `Owner: ${this.owner_name}`;
    }
    return "";
  }

  get archived() {
    return this.is_archived;
  }
  get_display_format(): string {
    return this.name;
  }

  get_detail_format(): string {
    return this.owner_string;
  }
  detailFormat() {
    return this.get_detail_format();
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      owner_id: this.owner_id,
      is_archived: this.is_archived,
      is_favorite: this.is_favorite,
      image_url: this.image_url,
      _owner_name: this.owner_name
    };
  }
  
  copy(copyMe: PlayerObject, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (!drop_ids) {
      this.owner_id = copyMe.owner_id;
    }
    this.is_archived = copyMe.is_archived;
    this.is_favorite = copyMe.is_favorite;
    this.image_url = copyMe.image_url;
  }

  clone(drop_ids = false): PlayerObject {
    const new_obj = new PlayerObject();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class ParentedBase extends ModelBase {
  parent_id = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.parent_id = obj.parent_id ? obj.parent_id : "";
    }
  }

  get archived() {
    return false;
  }
  get_display_format(): string {
    return this.name;
  }

  get_detail_format(): string {
    return "";
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      parent_id: this.parent_id,
    };
  }
  
  copy(copyMe: ParentedBase, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (!drop_ids) {
      this.parent_id = copyMe.parent_id;
    }
  }

  clone(drop_ids = false): ParentedBase {
    const new_obj = new ParentedBase();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export interface SmartHash {
  [data_type: string] : ModelBase[];
}

export interface TempHash {
  [data_type: string] : { objects: ModelBase[], last_accessed: number };
}

export interface ArchivesHash {
  [sb_id: string] : SmartHash;
}

export interface ArchivesTempHash {
  [sb_id: string] : TempHash;
}

export class OtherHash {
  getData(data_type: DataType, parent_id: string) {
    if (this.the_hash[data_type] && this.the_hash[data_type][parent_id]) {
      return this.the_hash[data_type][parent_id];
    }
    return [];
  }
  setData(data: ParentedBase[]) {
    if (data.length > 0) {
      const data_type = data[0].data_type;
      const parent_id = data[0].parent_id;
      if (!this.the_hash[data_type]) {
        this.the_hash[data_type] = {};
      }
      this.the_hash[data_type][parent_id] = data;
    }
  }
  appendData(data: ParentedBase | ParentedBase[]) {
    if (data instanceof ParentedBase) {
      const data_type = data.data_type;
      const parent_id = data.parent_id;
      if (!this.the_hash[data_type]) {
        this.the_hash[data_type] = {};
      }
      if (!this.the_hash[data_type][parent_id]) {
        this.the_hash[data_type][parent_id] = [];
      }
      this.the_hash[data_type][parent_id] = [...this.the_hash[data_type][parent_id],data];
    } else if (data.length > 0) {
      const data_type = data[0].data_type;
      const parent_id = data[0].parent_id;
      if (!this.the_hash[data_type]) {
        this.the_hash[data_type] = {};
      }
      if (!this.the_hash[data_type][parent_id]) {
        this.the_hash[data_type][parent_id] = [];
      }
      this.the_hash[data_type][parent_id] = [...this.the_hash[data_type][parent_id],...data];
    }
  }
  private the_hash: { [data_type: string] : { [parent_id: string]: ParentedBase[] }} = {};
}
